import React, { useEffect, useState, useCallback } from 'react';
import { Button, Typography, Box, CircularProgress, Alert, TextField } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../api/contests';
import DynamicFormFields, { FormField } from './DynamicFormFields';
import PassphraseCheck from '../components/PassphraseCheck';
import { debounce } from 'lodash';

// Define the Submission type
type Submission = {
    submission_id: number;
    username: string;
    first_name: string;
    last_name: string;
    contest_name: string;
    contest_id: number;
    submission_state: string;
    timestamp: string;
    entry_data: string;
};


// Evidence URL field
const evidenceUrlField: FormField = {
    field_name: 'evidence_url',
    display_label: 'Enter video evidence URL',
    data_type: 'text',
    required: false,
};

const ManageSubmissions: React.FC = () => {
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [formData, setFormData] = useState<{ [key: string]: any }>({});
    const [resultCalcRule, setResultCalcRule] = useState<any>(null);
    const [mainResultField, setMainResultField] = useState<string | null>(null);
    const [ranking, setRanking] = useState<number | null>(null);
    const [loadingForm, setLoadingForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [participantExists, setParticipantExists] = useState(true);
    const [loadingParticipantCheck, setLoadingParticipantCheck] = useState(false);
    const [adminComment, setAdminComment] = useState('');
    const [accessGranted, setAccessGranted] = useState(false);

    // Convert 'hh:mm:ss' to 'hhmmss'
    const convertTimeToHHMMSS = (time: string): string => {
        const timeParts = time.split(':');
        return timeParts.length === 3 ? timeParts.join('') : time;
    };

    // Calculate result based on the rule
    const calculateResult = (rule: any, data: { [key: string]: any }): number | string | null => {
        if (!rule || !rule.fields) return null;
        const { op, fields } = rule;
        const values = fields.map((field: string) => parseFloat(data[field]) || 0);

        switch (op) {
            case 'sum':
                return values.reduce((acc: number, val: number) => acc + val, 0);
            case 'diff':
                return values.reduce((acc: number, val: number) => acc - val);
            case 'mult':
                return values.reduce((acc: number, val: number) => acc * val, 1);
            case 'div':
                return values.reduce((acc: number, val: number) => (val !== 0 ? acc / val : acc));
            default:
                return null;
        }
    };

    const debounceCheckRanking = useCallback(
        debounce(async (contestId: number, grouping: string, newResult: any) => {
            const formattedResult = typeof newResult === 'string' && newResult.includes(':')
                ? convertTimeToHHMMSS(newResult)
                : newResult;
    
            try {
                const response = await axios.get(`${API_BASE_URL}/telegram/new-ranking`, {
                    params: { contest_id: contestId, grouping, new_result: formattedResult },
                });
                setRanking(response.data.ranking);
            } catch (error) {
                console.error("Error fetching ranking:", error);
                setRanking(null);
            }
        }, 300),
        []
    );
    
    // Trigger ranking calculation when form data changes
    useEffect(() => {
        const requiredResult = resultCalcRule
          ? calculateResult(resultCalcRule, formData)
          : formData[mainResultField || ''];
      
        if (selectedSubmission?.contest_id && formData.grouping && requiredResult !== undefined) {
          // Convert the result if it's in 'hh:mm:ss' format
          const formattedResult = typeof requiredResult === 'string' && requiredResult.includes(':')
            ? convertTimeToHHMMSS(requiredResult)
            : requiredResult;
      
          if (!isNaN(Number(formattedResult))) {
            debounceCheckRanking(selectedSubmission?.contest_id, formData.grouping, formattedResult);
          }
        }
    }, [formData, resultCalcRule, mainResultField, selectedSubmission, debounceCheckRanking]);
    
    // Fetch pending submissions
    useEffect(() => {
        const fetchSubmissions = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/admin/pending-submissions`);
                setSubmissions(response.data);
            } catch (error) {
                console.error("Error fetching submissions", error);
            }
        };
        fetchSubmissions();
    }, []);

    const handleSelectSubmission = async (submission: Submission) => {
        setSelectedSubmission(submission);
        const parsedEntryData = JSON.parse(submission.entry_data);
        setFormData(parsedEntryData);

        if (submission.contest_id) {
            setLoadingForm(true);
            try {
                const response = await axios.get(`${API_BASE_URL}/contests/${submission.contest_id}`);
                const metadata = response.data?.form_metadata;
                const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
                const fields = parsedMetadata.fields?.filter((f: any) => !f.non_entry_field) || [];
                fields.push(evidenceUrlField);
                setFormFields(fields);
                setMainResultField(fields.find((f: FormField) => f.main_result_field)?.field_name || null);
                setResultCalcRule(parsedMetadata.result_calc || null);
            } finally {
                setLoadingForm(false);
            }
        }

        setLoadingParticipantCheck(true);
        try {
            const checkResponse = await axios.get(`${API_BASE_URL}/admin/submission-by-existing-participant/${submission.submission_id}`);
            setParticipantExists(checkResponse.data.exists);
        } catch (error) {
            console.error("Error checking participant:", error);
        } finally {
            setLoadingParticipantCheck(false);
        }
    };

    const handleInputChange = (name: string, value: any) => {
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    // Handle rejecting a submission
    const handleRejectSubmission = async () => {
        if (!selectedSubmission) return;

        try {
            setSubmitting(true); // Start submission spinner
            await axios.post(`${API_BASE_URL}/admin/reject-submission`, {
                submission_id: selectedSubmission.submission_id,
                state_reason: adminComment, // Pass the admin comment
            });

            setSubmissions((prev) => prev.filter((sub) => sub.submission_id !== selectedSubmission.submission_id));
            setSelectedSubmission(null);
            setFormData({});
            setAdminComment(''); // Clear admin comment
            setSubmitting(false); // Stop spinner
            setSubmissionSuccess(true); // Show success notification
        } catch (error) {
            console.error("Error rejecting submission:", error);
            setSubmitting(false); // Stop spinner on error
        }
    };

    // Handle publishing a submission
    const handlePublishSubmission = async () => {
        if (!selectedSubmission) return;

        try {
            setSubmitting(true); // Start submission spinner
            await axios.post(`${API_BASE_URL}/admin/publish-submission`, {
                submission_id: selectedSubmission.submission_id,
                state_reason: adminComment, // Pass the admin comment
                entry_data: {...formData, ranking}, // Send updated form data
            });

            setSubmissions((prev) => prev.filter((sub) => sub.submission_id !== selectedSubmission.submission_id));
            setSelectedSubmission(null);
            setFormData({});
            setAdminComment(''); // Clear admin comment
            setSubmitting(false); // Stop spinner
            setSubmissionSuccess(true); // Show success notification
        } catch (error) {
            console.error("Error publishing submission:", error);
            setSubmitting(false); // Stop spinner on error
        }
    };

    return (
        !accessGranted ? <PassphraseCheck onAccessGranted={() => setAccessGranted(true)} />
        : <Box p={3}>
            <Typography variant="h5">Pending Submissions</Typography>

            {submissionSuccess && <Alert severity="success">✅ Submission processed successfully.</Alert>}

            {submissions.length > 0 ? (
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Contest</th>
                            <th>Submission Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {submissions.map(sub => (
                            <tr key={sub.submission_id}>
                                <td>{sub.submission_id}</td>
                                <td>{sub.username}</td>
                                <td>
                                    {sub.first_name} {sub.last_name}
                                </td>
                                <td>{sub.contest_name}</td>
                                <td>{sub.timestamp}</td>
                                <td>
                                    <Button onClick={() => handleSelectSubmission(sub)}>Manage</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : <Typography>No pending submissions.</Typography>}

            {selectedSubmission && (
                <>
                    <Box mb={5}>
                    <Typography variant="h5" gutterBottom>
                        Submission: {selectedSubmission.submission_id} {selectedSubmission.username} {selectedSubmission.first_name} - {selectedSubmission.contest_name}
                    </Typography>
                    </Box>
                    {loadingForm ? (
                        <CircularProgress />
                    ) : (
                        <Box mb={3}>
                    <DynamicFormFields 
                        formFields={formFields} 
                        contestId={selectedSubmission.contest_id.toString()} 
                        formData={formData} 
                        onInputChange={handleInputChange}
                        />
                    </Box>
                    )}
                    {loadingParticipantCheck ? (
                        <CircularProgress />
                    ) : !participantExists ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            ⚠️ Участник не зарегистрирован, добавьте нового участника.
                        </Alert>
                    ) : null}

                    <Box mb={3}>
                        <TextField
                            label="Admin Comment"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            value={adminComment}
                            onChange={(e) => setAdminComment(e.target.value)}
                        />
                    </Box>
                    <Box mb={3}>
                        <TextField label="Предварительный рейтинг" value={ranking ?? '—'}  fullWidth />
                    </Box>

                    {submitting ? (
                        <CircularProgress />
                    ) : (
                        <Box>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handlePublishSubmission}
                                disabled={!participantExists} // Disable if the participant is not registered
                            >
                                Publish
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleRejectSubmission}
                                style={{ marginLeft: '16px' }}
                            >
                                Reject
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ManageSubmissions;
